import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import SouqFlagPostModal from './SouqFlagPostModal';
import { extend } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import { isSouqDiscussion } from '../SouqGallery';
import Component from 'flarum/common/Component';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionsSearchItem from 'flarum/forum/components/DiscussionsSearchItem';

/*
 * FoF Upload creates its Uploader on TextEditor.
 * Keep a reference to the active composer's uploader
 * so the Souq mobile upload button can use it directly.
 */
extend(TextEditor.prototype, 'oncreate', function () {
  if (this.uploader && typeof this.uploader.upload === 'function') {
    window.__pinoytalksFofUploader = this.uploader;
  }
});
extend(TextEditor.prototype, 'onremove', function () {
  if (window.__pinoytalksFofUploader === this.uploader) {
    delete window.__pinoytalksFofUploader;
  }
});
export default class SouqPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.categoryPickerOpen = false;
    this.itemCategoryPickerOpen = false;
    this.selectedListingType = null;
    this.discussions = [];
    this.loading = true;
    this.loadingMore = false;
    this.listingError = false;

    /*
     * Souq expiry is intentionally separate from
     * the normal listing loader.
     */
    this.expiryData = {};
    this.expiryLoading = {};
    this.renewingListings = {};
    this.contactAvailability = {};
    this.contactAvailabilityLoading = {};
    this.contactSaving = {};

    /*
     * Keep the Souq photo counter synchronized
     * with FoF Upload composer content.
     */
    this.souqPhotoCounterTimer = window.setInterval(() => {
      this.updateSouqPhotoCounter();
    }, 400);

    /*
     * PinoyTalks Souq:
     * Maximum 5 images per listing.
     *
     * This only activates when the current
     * composer contains the "souq" tag.
     */
    if (!window.__pinoytalksSouqPhotoLimitInstalled) {
      window.__pinoytalksSouqPhotoLimitInstalled = true;
      const MAX_SOUQ_PHOTOS = 5;
      const getSouqComposerTags = () => {
        if (!app.composer || !app.composer.fields) {
          return [];
        }
        const tagsField = app.composer.fields.tags;
        if (Array.isArray(tagsField)) {
          return tagsField;
        }
        if (typeof tagsField === 'function') {
          try {
            const value = tagsField();
            return Array.isArray(value) ? value : [];
          } catch (error) {
            return [];
          }
        }
        return [];
      };
      const isSouqComposer = () => {
        return getSouqComposerTags().some(tag => {
          if (!tag) {
            return false;
          }
          const slug = typeof tag.slug === 'function' ? tag.slug() : tag.slug;
          return slug === 'souq';
        });
      };
      const getComposerContent = () => {
        if (!app.composer || !app.composer.fields) {
          return '';
        }
        const contentField = app.composer.fields.content;
        if (typeof contentField === 'function') {
          try {
            return String(contentField() || '');
          } catch (error) {
            return '';
          }
        }
        return String(contentField || '');
      };
      const countExistingPhotos = () => {
        const content = getComposerContent();

        /*
         * Cover FoF Upload's common
         * image templates.
         */
        const patterns = [/\[upl-image-preview\b/gi, /\[upl-image\b(?!-preview)/gi, /!\[[^\]]*\]\([^)]+\)/g, /\[IMG\]/gi];
        return patterns.reduce((total, pattern) => {
          const matches = content.match(pattern);
          return total + (matches ? matches.length : 0);
        }, 0);
      };
      const stopSouqUpload = (event, currentCount) => {
        const remaining = Math.max(0, MAX_SOUQ_PHOTOS - currentCount);
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        const input = event.target;

        /*
         * Clear selected files so FoF
         * never receives the rejected batch.
         */
        if (input && input.tagName === 'INPUT' && input.type === 'file') {
          try {
            input.value = '';
          } catch (error) {
            // Browser may prevent this.
          }
        }
        const message = remaining === 0 ? 'Maximum 5 photos per Souq listing. Remove a photo before uploading another.' : 'Maximum 5 photos per Souq listing. You can upload only ' + remaining + ' more photo' + (remaining === 1 ? '.' : 's.');
        app.alerts.show({
          type: 'error'
        }, message);
      };
      const guardSouqFiles = (files, event) => {
        if (!isSouqComposer()) {
          return;
        }
        const selectedImages = Array.from(files || []).filter(file => {
          return file && typeof file.type === 'string' && file.type.toLowerCase().startsWith('image/');
        });
        if (!selectedImages.length) {
          return;
        }
        const currentCount = countExistingPhotos();
        if (currentCount + selectedImages.length > MAX_SOUQ_PHOTOS) {
          stopSouqUpload(event, currentCount);
        }
      };

      /*
       * Upload-button / file picker.
       */
      document.addEventListener('change', event => {
        const input = event.target;
        if (!input || input.tagName !== 'INPUT' || input.type !== 'file') {
          return;
        }
        guardSouqFiles(input.files, event);
      }, true);

      /*
       * Drag-and-drop uploads.
       */
      document.addEventListener('drop', event => {
        if (!event.dataTransfer || !event.dataTransfer.files || !event.dataTransfer.files.length) {
          return;
        }
        guardSouqFiles(event.dataTransfer.files, event);
      }, true);
    }
    this.pageSize = 12;
    this.offset = 0;
    this.hasMore = true;
    this.filters = {
      keyword: '',
      itemCategory: 'all',
      category: 'all',
      status: 'all',
      location: '',
      minPrice: '',
      maxPrice: ''
    };
    this.pendingFilters = {
      keyword: '',
      itemCategory: 'all',
      category: 'all',
      status: 'all',
      location: '',
      minPrice: '',
      maxPrice: ''
    };
    this.loadListings();
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    app.setTitle('PinoyTalks Souq – OFW Marketplace in Saudi Arabia');
    app.setTitleCount(0);
  }
  postItem() {
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      return;
    }
    this.categoryPickerOpen = !this.categoryPickerOpen;
    m.redraw();
  }
  chooseListingType(typeSlug) {
    this.selectedListingType = typeSlug;
    this.categoryPickerOpen = false;
    this.itemCategoryPickerOpen = true;
    m.redraw();
  }
  backToListingTypes() {
    this.selectedListingType = null;
    this.itemCategoryPickerOpen = false;
    this.categoryPickerOpen = true;
    m.redraw();
  }
  onremove() {
    if (this.souqPhotoCounterTimer) {
      window.clearInterval(this.souqPhotoCounterTimer);
    }
    const counter = document.querySelector('.SouqPhotoCounter');
    if (counter) {
      counter.remove();
    }
    const mobileUploadButton = document.querySelector('.SouqMobileUploadButton');
    if (mobileUploadButton) {
      mobileUploadButton.remove();
    }
    const mobileFileInputCleanup = document.querySelector('.SouqMobileFileInput');
    if (mobileFileInputCleanup) {
      mobileFileInputCleanup.remove();
    }
  }
  moveCursorToSouqPhotos() {
    if (!app.composer || !app.composer.fields) {
      return false;
    }
    const contentField = app.composer.fields.content;
    let content = '';
    if (typeof contentField === 'function') {
      try {
        content = String(contentField() || '');
      } catch (error) {
        return false;
      }
    } else {
      content = String(contentField || '');
    }

    /*
     * Support both the current template
     * and older Souq listings/templates.
     */
    const photoMarkers = ['**Photos (Maximum 5):**', '**Photos:**'];
    let photoIndex = -1;
    for (const marker of photoMarkers) {
      const found = content.indexOf(marker);
      if (found !== -1) {
        photoIndex = found;
        break;
      }
    }
    if (photoIndex === -1) {
      return false;
    }

    /*
     * Prefer the position immediately before
     * the Souq Posting Reminder separator.
     */
    let position = content.indexOf('\n---', photoIndex);

    /*
     * Fallback: end of composer content.
     */
    if (position === -1) {
      position = content.length;
    }
    const textarea = document.querySelector('.Composer textarea') || document.querySelector('.TextEditor-editor');
    if (!textarea) {
      return false;
    }
    try {
      textarea.focus();
      textarea.setSelectionRange(position, position);
      return true;
    } catch (error) {
      console.warn('Could not move Souq cursor to Photos:', error);
      return false;
    }
  }
  getSouqPhotoCount() {
    if (!app.composer || !app.composer.fields) {
      return 0;
    }
    const contentField = app.composer.fields.content;
    let content = '';
    if (typeof contentField === 'function') {
      try {
        content = String(contentField() || '');
      } catch (error) {
        content = '';
      }
    } else {
      content = String(contentField || '');
    }
    const patterns = [/\[upl-image-preview\b/gi, /\[upl-image\b(?!-preview)/gi, /!\[[^\]]*\]\([^)]+\)/g, /\[IMG\]/gi];
    return patterns.reduce((total, pattern) => {
      const matches = content.match(pattern);
      return total + (matches ? matches.length : 0);
    }, 0);
  }
  isSouqComposerActive() {
    if (!app.composer || !app.composer.fields) {
      return false;
    }
    const tagsField = app.composer.fields.tags;
    let tags = [];
    if (Array.isArray(tagsField)) {
      tags = tagsField;
    } else if (typeof tagsField === 'function') {
      try {
        const value = tagsField();
        tags = Array.isArray(value) ? value : [];
      } catch (error) {
        tags = [];
      }
    }
    return tags.some(tag => {
      if (!tag) {
        return false;
      }
      const slug = typeof tag.slug === 'function' ? tag.slug() : tag.slug;
      return slug === 'souq';
    });
  }
  updateSouqPhotoCounter() {
    const existing = document.querySelector('.SouqPhotoCounter');
    if (!this.isSouqComposerActive()) {
      if (existing) {
        existing.remove();
      }
      return;
    }
    const composer = document.querySelector('.Composer');
    if (!composer) {
      return;
    }
    const editor = composer.querySelector('.TextEditor');
    if (!editor) {
      return;
    }
    let counter = composer.querySelector('.SouqPhotoCounter');
    if (!counter) {
      counter = document.createElement('div');
      counter.className = 'SouqPhotoCounter';

      /*
       * Do NOT place the counter inside the
       * TextEditor controls area. Flarum uses
       * that space for the composer toolbar.
       *
       * Put it near the top of the composer
       * instead.
       */
      const header = composer.querySelector('.ComposerBody-header');
      if (header && header.parentNode) {
        header.parentNode.insertBefore(counter, header.nextSibling);
      } else {
        const body = composer.querySelector('.ComposerBody');
        if (body) {
          body.insertBefore(counter, body.firstChild);
        } else {
          composer.insertBefore(counter, composer.firstChild);
        }
      }
    }
    const count = Math.min(this.getSouqPhotoCount(), 5);
    counter.classList.toggle('is-full', count >= 5);
    const isMobile = window.innerWidth <= 767;
    counter.textContent = count >= 5 ? '📷 Photos: 5 / 5 — Maximum reached' : '📷 Photos: ' + count + ' / 5';

    /*
     * Dedicated mobile upload button.
     * It triggers FoF Upload's existing control.
     */
    let mobileUploadButton = composer.querySelector('.SouqMobileUploadButton');
    if (!mobileUploadButton) {
      mobileUploadButton = document.createElement('button');
      mobileUploadButton.type = 'button';
      mobileUploadButton.className = 'SouqMobileUploadButton';
      mobileUploadButton.textContent = '⬆ Upload Photos';
      counter.insertAdjacentElement('afterend', mobileUploadButton);

      /*
       * Dedicated hidden image picker.
       * The selected files are passed directly
       * into FoF Upload's real Uploader instance.
       */
      let mobileFileInput = composer.querySelector('.SouqMobileFileInput');
      if (!mobileFileInput) {
        mobileFileInput = document.createElement('input');
        mobileFileInput.type = 'file';
        mobileFileInput.multiple = true;
        mobileFileInput.accept = 'image/*';
        mobileFileInput.className = 'SouqMobileFileInput';
        mobileFileInput.style.display = 'none';
        mobileUploadButton.insertAdjacentElement('afterend', mobileFileInput);
        mobileFileInput.addEventListener('change', async () => {
          const files = mobileFileInput.files;
          if (!files || files.length === 0) {
            return;
          }
          const uploader = window.__pinoytalksFofUploader;
          if (!uploader || typeof uploader.upload !== 'function') {
            mobileFileInput.value = '';
            app.alerts.show({
              type: 'error'
            }, 'Upload service is not ready. Please close and reopen the listing composer.');
            return;
          }
          try {
            /*
             * Android may change editor focus
             * while the Gallery is open.
             * Restore the Photos insertion point.
             */
            this.moveCursorToSouqPhotos();

            /*
             * true = insert FoF Upload
             * BBCode into the composer.
             */
            await uploader.upload(files, true);
          } catch (error) {
            console.error('PinoyTalks Souq mobile upload error:', error);
          } finally {
            /*
             * Allows selecting the
             * same photo again later.
             */
            mobileFileInput.value = '';
          }
        });
      }
      mobileUploadButton.addEventListener('click', () => {
        const currentCount = this.getSouqPhotoCount();
        if (currentCount >= 5) {
          app.alerts.show({
            type: 'error'
          }, 'Maximum 5 photos per Souq listing.');
          return;
        }
        this.moveCursorToSouqPhotos();
        mobileFileInput.click();
      });
    }
    mobileUploadButton.disabled = count >= 5;
    mobileUploadButton.textContent = count >= 5 ? '✓ Maximum 5 Photos' : '⬆ Upload Photos';
  }
  async openListingComposer(itemCategorySlug) {
    const listingTypeSlug = this.selectedListingType;
    if (!listingTypeSlug) {
      this.itemCategoryPickerOpen = false;
      this.categoryPickerOpen = true;
      m.redraw();
      return;
    }
    const templates = {
      'buy-sell': `**Price:** SAR

**Location:**

**Condition:** New / Like New / Good / Fair

**Negotiable:** Yes / No

**Description:**
Describe the item, age, condition, inclusions, and other important details.

**Pickup / Delivery:**

**Photos (Maximum 5):**
Upload up to 5 clear photos of the actual item.
`,
      'final-exit-sale': `**Price:** SAR

**Location:**

**Condition:** New / Like New / Good / Fair

**Final Exit / Moving Date:**

**Negotiable:** Yes / No

**Description:**
Describe the item, age, condition, inclusions, and other important details.

**Pickup / Delivery:**

**Photos (Maximum 5):**
Upload up to 5 clear photos of the actual item.
`,
      'looking-for': `**Budget:** SAR

**Location:**

**Looking For:**
Describe exactly what item you need.

**Preferred Condition:**

**Other Details:**
Add model, size, brand, or other requirements if applicable.
`,
      'free-giveaways': `**Price:** FREE

**Location:**

**Condition:** New / Like New / Good / Fair

**Description:**
Describe the item and its condition.

**Pickup Details:**

**Photos (Maximum 5):**
Upload up to 5 clear photos of the actual item.
`
    };
    try {
      await app.store.find('tags', {
        include: 'parent'
      });
      const allTags = app.store.all('tags');
      const souqTag = allTags.find(tag => tag.slug() === 'souq');
      const listingTypeTag = allTags.find(tag => tag.slug() === listingTypeSlug);
      const itemCategoryTag = allTags.find(tag => tag.slug() === itemCategorySlug);
      const activeTag = allTags.find(tag => tag.slug() === 'souq-active');
      if (!souqTag) {
        throw new Error('Souq tag was not found.');
      }
      if (!listingTypeTag) {
        throw new Error('Souq listing type was not found.');
      }
      if (!itemCategoryTag) {
        throw new Error('Souq item category was not found.');
      }
      if (!activeTag) {
        throw new Error('Souq Active status tag was not found.');
      }
      await app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
        user: app.session.user,
        originalContent: (templates[listingTypeSlug] || templates['buy-sell']) + `

---

**⚠️ Souq Posting Reminder**

By posting, you confirm that your listing complies with applicable laws and PinoyTalks Souq rules.

Illegal, restricted, fraudulent, counterfeit, stolen, misleading, or unsafe listings are prohibited and may be removed by PinoyTalks.
`,
        titlePlaceholder: 'What are you selling?'
      });
      app.composer.fields.tags = [souqTag, listingTypeTag, itemCategoryTag, activeTag];
      this.itemCategoryPickerOpen = false;
      this.categoryPickerOpen = false;
      this.selectedListingType = null;
      app.composer.show();
      m.redraw();
    } catch (error) {
      console.error('PinoyTalks Souq composer error:', error);
      app.alerts.show({
        type: 'error'
      }, 'Unable to open Souq listing form: ' + (error.message || 'Unknown error'));
    }
  }
  async loadListings(reset) {
    if (reset === void 0) {
      reset = true;
    }
    if (reset) {
      this.loading = true;
      this.loadingMore = false;
      this.listingError = false;
      this.offset = 0;
      this.hasMore = true;
      this.discussions = [];
    } else {
      this.loadingMore = true;
    }
    try {
      const batch = await app.store.find('discussions', {
        filter: {
          tag: 'souq'
        },
        include: 'user,tags,firstPost',
        page: {
          limit: this.pageSize,
          offset: this.offset
        }
      });

      /*
       * Avoid duplicates if Flarum already has
       * any of these discussions in its Store.
       */
      const unique = new Map();
      this.discussions.forEach(discussion => {
        unique.set(String(discussion.id()), discussion);
      });
      batch.forEach(discussion => {
        unique.set(String(discussion.id()), discussion);
      });
      this.discussions = Array.from(unique.values());

      /*
       * Keep marketplace cards ordered by
       * listing creation date.
       */
      this.discussions.sort((a, b) => {
        const aDate = a.createdAt ? a.createdAt() : null;
        const bDate = b.createdAt ? b.createdAt() : null;
        return new Date(bDate || 0) - new Date(aDate || 0);
      });
      this.offset += batch.length;

      /*
       * Fewer than pageSize means we reached
       * the end of the available listings.
       */
      this.hasMore = batch.length === this.pageSize;
    } catch (error) {
      console.error('PinoyTalks Souq listing error:', error);
      this.listingError = true;
    } finally {
      this.loading = false;
      this.loadingMore = false;
      m.redraw();
    }
  }
  async loadMoreListings() {
    if (this.loadingMore || !this.hasMore) {
      return;
    }
    await this.loadListings(false);
  }
  categoryMeta(discussion) {
    const categories = {
      'buy-sell': {
        label: 'Buy & Sell',
        icon: '🛒'
      },
      'final-exit-sale': {
        label: 'Final Exit Sale',
        icon: '📦'
      },
      'looking-for': {
        label: 'Looking For',
        icon: '🔍'
      },
      'free-giveaways': {
        label: 'Free & Giveaways',
        icon: '🎁'
      }
    };
    const tags = discussion.tags ? discussion.tags() || [] : [];
    for (const tag of tags) {
      const slug = tag.slug();
      if (categories[slug]) {
        return categories[slug];
      }
    }
    return {
      label: 'Souq',
      icon: '🛍️'
    };
  }
  getImage(discussion) {
    const post = discussion.firstPost ? discussion.firstPost() : null;
    if (!post || post === false) {
      return null;
    }
    const html = post.contentHtml ? post.contentHtml() || '' : '';
    if (!html) {
      return null;
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    const images = Array.from(container.querySelectorAll('img'));
    const productImage = images.find(img => {
      const src = (img.getAttribute('src') || '').trim();
      const className = (img.getAttribute('class') || '').toLowerCase();
      if (!src) {
        return false;
      }

      // Ignore emoji images such as the
      // Souq Posting Reminder warning icon.
      if (className.includes('emoji') || src.toLowerCase().includes('twemoji') || src.toLowerCase().includes('/emoji/')) {
        return false;
      }
      return true;
    });
    return productImage ? productImage.getAttribute('src') : null;
  }
  listingField(discussion, label) {
    const post = discussion.firstPost ? discussion.firstPost() : null;
    if (!post) return '';

    // Preserve paragraph boundaries: contentPlain may flatten the entire post.
    const html = post.contentHtml ? post.contentHtml() || '' : '';
    const container = document.createElement('div');
    container.innerHTML = html.replace(/<br\s*\/?\s*>|<\/(?:p|div|li|h[1-6])>/gi, '\n');
    const text = html ? container.textContent : post.contentPlain ? post.contentPlain() || '' : '';
    const line = text.split(/\r?\n/).find(line => line.replace(/\*\*/g, '').trim().toLowerCase().startsWith(label.toLowerCase() + ':'));
    if (!line) return '';
    return line.replace(/\*\*/g, '').trim().slice(label.length + 1).split(/(?:Description|Condition|Phone|WhatsApp|Email|Contact|Seller|Price|Location)\s*:/i)[0].trim();
  }
  getLocation(discussion) {
    const location = this.listingField(discussion, 'Location');
    // Omit ambiguous/contact-containing values instead of displaying a body excerpt.
    return location.length <= 100 && !/(?:@|https?:|www\.|\+?\d[\d ()-]{6,}\d)/i.test(location) ? location : '';
  }
  async ensureExpiryLoaded(discussion) {
    const id = String(discussion.id());

    /*
     * Already loaded or currently loading.
     */
    if (Object.prototype.hasOwnProperty.call(this.expiryData, id) || this.expiryLoading[id]) {
      return;
    }
    this.expiryLoading[id] = true;
    try {
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/souq/expiries?ids=' + encodeURIComponent(id)
      });
      this.expiryData[id] = response && response.data && response.data[id] ? response.data[id] : null;
    } catch (error) {
      console.error('PinoyTalks Souq expiry error:', error);

      /*
       * Expiry failure must never affect
       * the normal marketplace listing.
       */
      this.expiryData[id] = null;
    } finally {
      delete this.expiryLoading[id];
      m.redraw();
    }
  }
  getExpiryMeta(discussion) {
    const id = String(discussion.id());
    return this.expiryData[id] || null;
  }
  getExpiryLabel(discussion) {
    const meta = this.getExpiryMeta(discussion);
    if (!meta || !meta.expiresAt) {
      return '';
    }
    if (meta.expired) {
      return 'Expired';
    }
    const expires = new Date(meta.expiresAt);
    const now = new Date();
    const milliseconds = expires.getTime() - now.getTime();
    const days = Math.max(0, Math.ceil(milliseconds / (1000 * 60 * 60 * 24)));
    if (days === 0) {
      return 'Expires today';
    }
    if (days === 1) {
      return 'Expires in 1 day';
    }
    return 'Expires in ' + days + ' days';
  }
  async renewListing(discussion) {
    const id = String(discussion.id());
    if (this.renewingListings[id]) {
      return;
    }
    this.renewingListings[id] = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/souq/renew/' + discussion.id()
      });
      if (response && response.expiresAt) {
        this.expiryData[id] = {
          expiresAt: response.expiresAt,
          renewedAt: new Date().toISOString(),
          expired: false
        };
      }

      /*
       * Refresh this discussion so the Active
       * status tag changed by the backend is
       * reflected in Flarum's frontend store.
       */
      try {
        await app.store.find('discussions', discussion.id(), {
          include: 'tags'
        });
      } catch (refreshError) {
        console.warn('Souq renewed, but discussion refresh failed:', refreshError);
      }
      app.alerts.show({
        type: 'success'
      }, 'Listing renewed for another 30 days.');
    } catch (error) {
      console.error('PinoyTalks Souq renewal error:', error);
      const message = error && error.response && error.response.errors && error.response.errors[0] && error.response.errors[0].detail ? error.response.errors[0].detail : 'Unable to renew this listing.';
      app.alerts.show({
        type: 'error'
      }, message);
    } finally {
      delete this.renewingListings[id];
      m.redraw();
    }
  }
  async ensureContactAvailability(discussion, force) {
    if (force === void 0) {
      force = false;
    }
    const id = String(discussion.id());
    if (!force && (Object.prototype.hasOwnProperty.call(this.contactAvailability, id) || this.contactAvailabilityLoading[id])) {
      return;
    }
    this.contactAvailabilityLoading[id] = true;
    try {
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/souq/contact-availability?ids=' + encodeURIComponent(id)
      });
      this.contactAvailability[id] = Boolean(response && response.data && response.data[id] && response.data[id].whatsappAvailable);
    } catch (error) {
      console.error('PinoyTalks Souq WhatsApp availability error:', error);
      this.contactAvailability[id] = false;
    } finally {
      delete this.contactAvailabilityLoading[id];
      m.redraw();
    }
  }
  hasWhatsAppContact(discussion) {
    return Boolean(this.contactAvailability[String(discussion.id())]);
  }
  async configureWhatsApp(discussion) {
    const id = String(discussion.id());
    if (this.contactSaving[id]) {
      return;
    }
    const number = window.prompt('Enter your WhatsApp number.\n\n' + 'Example: 0501234567 or +966501234567\n\n' + 'Your number will NOT be displayed publicly.');
    if (number === null) {
      return;
    }
    const cleanNumber = number.trim();
    if (!cleanNumber) {
      app.alerts.show({
        type: 'error'
      }, 'Please enter your WhatsApp number.');
      return;
    }
    this.contactSaving[id] = true;
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/souq/contact/' + discussion.id(),
        body: {
          enabled: true,
          number: cleanNumber
        }
      });

      /*
       * Listing is currently active/
       * contactable, so update the UI
       * immediately.
       */
      this.contactAvailability[id] = true;
      app.alerts.show({
        type: 'success'
      }, 'WhatsApp contact enabled. Your number is not displayed publicly.');
    } catch (error) {
      console.error('PinoyTalks Souq WhatsApp save error:', error);
      app.alerts.show({
        type: 'error'
      }, 'Unable to save the WhatsApp number. Please check the number and try again.');
    } finally {
      delete this.contactSaving[id];
      m.redraw();
    }
  }
  async disableWhatsApp(discussion) {
    const id = String(discussion.id());
    if (this.contactSaving[id]) {
      return;
    }
    const confirmed = window.confirm('Disable WhatsApp contact for this listing?');
    if (!confirmed) {
      return;
    }
    this.contactSaving[id] = true;
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/souq/contact/' + discussion.id(),
        body: {
          enabled: false
        }
      });
      this.contactAvailability[id] = false;
      app.alerts.show({
        type: 'success'
      }, 'WhatsApp contact disabled.');
    } catch (error) {
      console.error('PinoyTalks Souq WhatsApp disable error:', error);
      app.alerts.show({
        type: 'error'
      }, 'Unable to disable WhatsApp contact.');
    } finally {
      delete this.contactSaving[id];
      m.redraw();
    }
  }
  openWhatsAppSeller(discussion) {
    /*
     * Backend still verifies that the
     * visitor is logged in and that the
     * listing is active.
     */
    if (!app.session.user) {
      app.alerts.show({
        type: 'error'
      }, 'Please log in to contact the seller on WhatsApp.');
      return;
    }
    const url = app.forum.attribute('apiUrl') + '/souq/contact/' + discussion.id() + '/whatsapp';
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  getStatus(discussion) {
    const tags = discussion.tags ? discussion.tags() || [] : [];
    const statusMap = {
      'souq-active': {
        key: 'active',
        label: 'ACTIVE'
      },
      'souq-reserved': {
        key: 'reserved',
        label: 'RESERVED'
      },
      'souq-sold': {
        key: 'sold',
        label: 'SOLD'
      }
    };
    for (const tag of tags) {
      const slug = tag.slug();
      if (statusMap[slug]) {
        return statusMap[slug];
      }
    }

    /*
     * Older Souq listings created before the
     * status system are treated as Active.
     */
    return {
      key: 'active',
      label: 'ACTIVE'
    };
  }
  canManageStatus(discussion) {
    const actor = app.session.user;
    if (!actor) {
      return false;
    }
    const seller = discussion.user ? discussion.user() : null;
    if (!seller) {
      return false;
    }
    if (String(actor.id()) === String(seller.id())) {
      return true;
    }
    return actor.isAdmin ? actor.isAdmin() : false;
  }
  async setListingStatus(discussion, status) {
    const id = discussion.id();
    this.updatingStatus = this.updatingStatus || {};
    this.updatingStatus[id] = true;
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/souq/status/' + id,
        body: {
          status: status
        }
      });

      /*
       * Reload Souq so the new tag relationship
       * is immediately reflected in the card.
       */
      await this.loadListings();
      app.alerts.show({
        type: 'success'
      }, 'Listing status updated.');
    } catch (error) {
      console.error('PinoyTalks Souq status error:', error);
      app.alerts.show({
        type: 'error'
      }, 'Unable to update listing status.');
    } finally {
      delete this.updatingStatus[id];
      m.redraw();
    }
  }
  getPrice(discussion) {
    const title = discussion.title() || '';
    const meta = this.categoryMeta(discussion);
    if (meta.label === 'Free & Giveaways') {
      return 'FREE';
    }
    let match = title.match(/\bSAR\s*[\d,.]+/i);
    if (match) {
      return match[0].toUpperCase();
    }
    match = title.match(/\b[\d,.]+\s*SAR\b/i);
    if (match) {
      return match[0].toUpperCase();
    }
    const price = this.listingField(discussion, 'Price');
    const value = price.match(/^(?:SAR\s*)?([\d]+(?:[,.]\d+)*)(?:\s*SAR)?$/i);
    return /^free$/i.test(price) ? 'FREE' : value ? 'SAR ' + value[1] : '';
  }
  getDate(discussion) {
    const date = discussion.createdAt ? discussion.createdAt() : null;
    if (!date) {
      return '';
    }
    return new Date(date).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  }
  category(icon, title, description, slug) {
    return m("a", {
      className: "SouqCategory",
      href: '/t/' + slug
    }, m("span", {
      className: "SouqCategory-icon"
    }, icon), m("div", null, m("strong", null, title), m("small", null, description)));
  }
  pickerButton(icon, title, description, slug) {
    return m("button", {
      className: "SouqPickerButton",
      onclick: () => this.chooseListingType(slug)
    }, m("span", {
      className: "SouqPickerButton-icon"
    }, icon), m("span", null, m("strong", null, title), m("small", null, description)));
  }
  getItemCategorySlug(discussion) {
    const validCategories = ['vehicles', 'phones-electronics', 'fashion', 'home-living', 'sports-hobbies', 'jewelry-watches', 'baby-kids', 'other-items'];
    const tags = discussion.tags ? discussion.tags() || [] : [];
    for (const tag of tags) {
      const slug = tag.slug();
      if (validCategories.includes(slug)) {
        return slug;
      }
    }
    return '';
  }
  itemCategoryMeta(discussion) {
    const categories = {
      'vehicles': {
        label: 'Vehicles',
        icon: '🚗'
      },
      'phones-electronics': {
        label: 'Phones & Electronics',
        icon: '📱'
      },
      'fashion': {
        label: 'Fashion',
        icon: '👕'
      },
      'home-living': {
        label: 'Home & Living',
        icon: '🏠'
      },
      'sports-hobbies': {
        label: 'Sports & Hobbies',
        icon: '⚽'
      },
      'jewelry-watches': {
        label: 'Jewelry & Watches',
        icon: '⌚'
      },
      'baby-kids': {
        label: 'Baby & Kids',
        icon: '🧸'
      },
      'other-items': {
        label: 'Other Items',
        icon: '📦'
      }
    };
    const slug = this.getItemCategorySlug(discussion);
    return categories[slug] || {
      label: 'Other Items',
      icon: '📦'
    };
  }
  getCategorySlug(discussion) {
    const validCategories = ['buy-sell', 'final-exit-sale', 'looking-for', 'free-giveaways'];
    const tags = discussion.tags ? discussion.tags() || [] : [];
    for (const tag of tags) {
      const slug = tag.slug();
      if (validCategories.includes(slug)) {
        return slug;
      }
    }
    return '';
  }
  getNumericPrice(discussion) {
    const category = this.getCategorySlug(discussion);

    /*
     * Giveaways always have a numeric
     * marketplace price of zero.
     */
    if (category === 'free-giveaways') {
      return 0;
    }
    const post = discussion.firstPost ? discussion.firstPost() : null;
    const plainText = post && post.contentPlain ? post.contentPlain() || '' : '';
    const title = discussion.title ? discussion.title() || '' : '';

    /*
     * Search the title and listing body.
     */
    const searchableText = title + '\n' + plainText;

    /*
     * Supports:
     *
     * SAR 1,250
     * SAR1250
     * 1,250 SAR
     * Price: SAR 1,250
     * Price: 1250
     */
    const patterns = [/Price\s*:\s*SAR\s*([\d,.]+)/i, /Price\s*:\s*([\d,.]+)\s*SAR/i, /Price\s*:\s*([\d,.]+)/i, /\bSAR\s*([\d,.]+)/i, /\b([\d,.]+)\s*SAR\b/i];
    for (const pattern of patterns) {
      const match = searchableText.match(pattern);
      if (!match) {
        continue;
      }
      const value = Number(match[1].replace(/,/g, ''));
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
    }
    return null;
  }
  filteredListings() {
    const keywordTerms = (this.filters.keyword || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    const itemCategory = this.filters.itemCategory;
    const category = this.filters.category;
    const status = this.filters.status;
    const location = this.filters.location.trim().toLowerCase();
    const minPrice = this.pendingFilters.minPrice === '' ? null : Number(this.filters.minPrice);
    const maxPrice = this.pendingFilters.maxPrice === '' ? null : Number(this.filters.maxPrice);
    return this.discussions.filter(discussion => {
      if (keywordTerms.length) {
        const post = discussion.firstPost ? discussion.firstPost() : null;
        const plainText = post && post.contentPlain ? post.contentPlain() || '' : '';
        const title = discussion.title ? discussion.title() || '' : '';
        const locationText = this.getLocation(discussion) || '';
        const categoryText = this.categoryMeta(discussion).label || '';
        const seller = discussion.user ? discussion.user() : null;
        const sellerName = seller && seller.displayName ? seller.displayName() : '';
        const searchableText = (title + ' ' + plainText + ' ' + locationText + ' ' + categoryText + ' ' + sellerName).toLowerCase().replace(/\s+/g, ' ').trim();
        const matchesAllTerms = keywordTerms.every(term => searchableText.includes(term));
        if (!matchesAllTerms) {
          return false;
        }
      }
      if (itemCategory !== 'all' && this.getItemCategorySlug(discussion) !== itemCategory) {
        return false;
      }
      if (category !== 'all' && this.getCategorySlug(discussion) !== category) {
        return false;
      }
      if (status !== 'all' && this.getStatus(discussion).key !== status) {
        return false;
      }
      if (location) {
        const post = discussion.firstPost ? discussion.firstPost() : null;
        const plainText = post && post.contentPlain ? post.contentPlain() || '' : '';
        const htmlText = post && post.contentHtml ? post.contentHtml() || '' : '';
        const title = discussion.title ? discussion.title() || '' : '';
        const searchableText = (title + ' ' + plainText + ' ' + htmlText).toLowerCase().replace(/\s+/g, ' ').trim();
        const wantedLocation = location.toLowerCase().replace(/\s+/g, ' ').trim();
        if (!searchableText.includes(wantedLocation)) {
          return false;
        }
      }
      if (minPrice !== null || maxPrice !== null) {
        const price = this.getNumericPrice(discussion);
        if (price === null) {
          return false;
        }
        if (minPrice !== null && price < minPrice) {
          return false;
        }
        if (maxPrice !== null && price > maxPrice) {
          return false;
        }
      }
      return true;
    });
  }
  applyFilters() {
    this.filters = {
      ...this.pendingFilters
    };
    m.redraw();
    setTimeout(() => {
      const listings = document.querySelector('.SouqLatest');
      if (listings) {
        listings.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 50);
  }
  clearFilters() {
    const emptyFilters = {
      keyword: '',
      itemCategory: 'all',
      category: 'all',
      status: 'all',
      location: '',
      minPrice: '',
      maxPrice: ''
    };
    this.filters = {
      ...emptyFilters
    };
    this.pendingFilters = {
      ...emptyFilters
    };
    m.redraw();
  }
  isOwnListing(discussion) {
    const actor = app.session.user;
    const seller = discussion.user ? discussion.user() : null;
    if (!actor || !seller) {
      return false;
    }
    return String(actor.id()) === String(seller.id());
  }
  canMessageSeller(discussion) {
    const seller = discussion.user ? discussion.user() : null;
    if (!seller) {
      return false;
    }

    /*
     * Guests still see Contact Seller.
     * Clicking it will ask them to log in.
     */
    if (!app.session.user) {
      return true;
    }
    if (this.isOwnListing(discussion)) {
      return false;
    }
    const canStart = app.forum.attribute('canStartPrivateDiscussion');
    const canMessageBlockers = app.forum.attribute('canStartPrivateDiscussionWithBlockers');
    const sellerBlocksMessages = seller.blocksPd ? seller.blocksPd() : false;
    return Boolean(canStart && (!sellerBlocksMessages || canMessageBlockers));
  }
  reportListing(discussion) {
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      return;
    }
    if (this.isOwnListing(discussion)) {
      app.alerts.show({
        type: 'info'
      }, 'You cannot report your own listing.');
      return;
    }
    const post = discussion.firstPost ? discussion.firstPost() : null;
    if (!post || post === false) {
      app.alerts.show({
        type: 'error'
      }, 'This listing cannot be reported right now.');
      return;
    }
    const canFlag = post.canFlag ? post.canFlag() : false;
    if (!canFlag) {
      app.alerts.show({
        type: 'error'
      }, 'You do not have permission to report this listing.');
      return;
    }
    app.modal.show(() => import(/* webpackChunkName: 'forum/components/SouqFlagPostModal', webpackMode: 'lazy-once' */ './SouqFlagPostModal'), {
      post: post
    });
  }
  contactSeller(discussion) {
    const seller = discussion.user ? discussion.user() : null;
    if (!seller) {
      app.alerts.show({
        type: 'error'
      }, 'Seller information is unavailable.');
      return;
    }
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      return;
    }
    if (this.isOwnListing(discussion)) {
      return;
    }
    if (!this.canMessageSeller(discussion)) {
      app.alerts.show({
        type: 'error'
      }, 'Private messaging is unavailable for this seller.');
      return;
    }
    const sellerName = seller.displayName ? seller.displayName() : 'Seller';
    const listingTitle = discussion.title ? discussion.title() : 'Souq listing';
    const shortTitle = listingTitle.length > 80 ? listingTitle.slice(0, 77) + '...' : listingTitle;
    const discussionPath = app.route.discussion(discussion);
    const listingUrl = window.location.origin + discussionPath;
    const message = `Hi ${sellerName},

I'm interested in your listing:

${listingTitle}

Is this still available?

View listing:
${listingUrl}`;

    /*
     * encodeURIComponent uses %20 for spaces
     * instead of "+".
     */
    const query = '?recipientUsers=' + encodeURIComponent(String(seller.id())) + '&title=' + encodeURIComponent('Souq inquiry: ' + shortTitle) + '&content=' + encodeURIComponent(message) + '&redirect=' + encodeURIComponent(discussionPath);
    m.route.set('/private/composer' + query);
  }
  itemCategoryButton(icon, title, description, slug) {
    return m("button", {
      className: "SouqPickerButton",
      onclick: () => this.openListingComposer(slug)
    }, m("span", {
      className: "SouqPickerButton-icon"
    }, icon), m("span", null, m("strong", null, title), m("small", null, description)));
  }
  listingCard(discussion) {
    const meta = this.itemCategoryMeta(discussion);
    const image = this.getImage(discussion);
    const location = this.getLocation(discussion);
    const price = this.getPrice(discussion);
    const normalStatus = this.getStatus(discussion);
    const expiry = this.getExpiryMeta(discussion);
    const status = normalStatus.key !== 'sold' && expiry && expiry.expired ? {
      key: 'expired',
      label: 'EXPIRED'
    } : normalStatus;
    return m("a", {
      className: 'SouqListing SouqListing--' + status.key,
      href: app.route.discussion(discussion),
      oncreate: () => this.ensureExpiryLoaded(discussion)
    }, m("div", {
      className: "SouqListing-photo"
    }, m("img", {
      src: image || '/images/souq/no-product-photo.png',
      alt: "",
      loading: "lazy",
      onerror: e => {
        const img = e.currentTarget || e.target;
        if (!img.dataset.fallbackApplied) {
          img.dataset.fallbackApplied = '1';
          img.src = '/images/souq/no-product-photo.png';
        }
      }
    }), m("span", {
      className: 'SouqStatusBadge SouqStatusBadge--' + status.key
    }, status.label)), m("div", {
      className: "SouqListing-content"
    }, m("h3", null, discussion.title()), price && m("div", {
      className: "SouqListing-price"
    }, price), location && m("div", {
      className: "SouqListing-location"
    }, location), m("div", {
      className: "SouqListing-meta"
    }, m("span", {
      className: "SouqListing-category"
    }, meta.label), m("span", null, this.getDate(discussion)))));
  }
  listingDetailControls(discussion) {
    const normalStatus = this.getStatus(discussion);
    const expiryMeta = this.getExpiryMeta(discussion);
    const expired = Boolean(expiryMeta && expiryMeta.expired);

    /*
     * SOLD takes precedence over expiry.
     */
    const status = normalStatus.key === 'sold' ? normalStatus : expired ? {
      key: 'expired',
      label: 'EXPIRED'
    } : normalStatus;
    const canManage = this.canManageStatus(discussion);
    const updating = this.updatingStatus && this.updatingStatus[discussion.id()];
    return m("div", {
      className: "SouqListingDetailControls"
    }, !this.isOwnListing(discussion) && m("div", {
      className: "SouqReportListing"
    }, m("button", {
      className: "SouqReportButton",
      onclick: () => this.reportListing(discussion)
    }, "\u2691 Report Listing")), canManage && status.key === 'expired' && m("div", {
      className: "SouqRenewListing"
    }, m("button", {
      className: "SouqRenewButton",
      disabled: this.renewingListings[String(discussion.id())],
      onclick: () => this.renewListing(discussion)
    }, this.renewingListings[String(discussion.id())] ? 'RENEWING...' : '↻ RENEW FOR 30 DAYS')), canManage && status.key !== 'sold' && status.key !== 'expired' && m("div", {
      className: "SouqWhatsAppOwner"
    }, m("div", {
      className: "SouqWhatsAppOwner-heading"
    }, "Private WhatsApp Contact"), m("div", {
      className: "SouqWhatsAppOwner-status"
    }, this.hasWhatsAppContact(discussion) ? '✓ Enabled — number hidden publicly' : 'Not enabled'), m("div", {
      className: "SouqWhatsAppOwner-actions"
    }, m("button", {
      disabled: this.contactSaving[String(discussion.id())],
      onclick: () => this.configureWhatsApp(discussion)
    }, this.hasWhatsAppContact(discussion) ? 'UPDATE WHATSAPP' : 'ENABLE WHATSAPP'), this.hasWhatsAppContact(discussion) && m("button", {
      className: "is-disable",
      disabled: this.contactSaving[String(discussion.id())],
      onclick: () => this.disableWhatsApp(discussion)
    }, "DISABLE")), m("small", null, "Your number is never shown on the public listing. Logged-in buyers can only use the WhatsApp button to contact you.")), canManage && status.key !== 'expired' && m("div", {
      className: "SouqStatusControls"
    }, m("div", {
      className: "SouqStatusControls-label"
    }, "Listing status"), m("div", {
      className: "SouqStatusControls-buttons"
    }, m("button", {
      disabled: updating,
      className: 'SouqStatusButton ' + (status.key === 'active' ? 'is-selected' : ''),
      onclick: () => this.setListingStatus(discussion, 'active')
    }, "Active"), m("button", {
      disabled: updating,
      className: 'SouqStatusButton ' + (status.key === 'reserved' ? 'is-selected' : ''),
      onclick: () => this.setListingStatus(discussion, 'reserved')
    }, "Reserved"), m("button", {
      disabled: updating,
      className: 'SouqStatusButton ' + (status.key === 'sold' ? 'is-selected' : ''),
      onclick: () => this.setListingStatus(discussion, 'sold')
    }, "Sold"))));
  }
  view() {
    const filteredListings = this.filteredListings();
    return m("div", {
      className: "SouqPage"
    }, m("section", {
      className: "SouqHero"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "SouqHero-inner"
    }, m("div", {
      className: "SouqHero-label"
    }, "OFW COMMUNITY MARKETPLACE"), m("h1", null, "PinoyTalks Souq"), m("div", {
      className: "SouqHero-tagline"
    }, "Buy \u2022 Sell \u2022 Find \u2022 Connect"), m("p", null, "A community marketplace for Filipinos living and working in Saudi Arabia."), m("button", {
      className: "SouqPostButton",
      onclick: () => this.postItem()
    }, "+ POST AN ITEM"), this.categoryPickerOpen && m("div", {
      className: "SouqCategoryPicker"
    }, m("h3", null, "What are you posting?"), m("p", null, "Choose one listing type."), m("div", {
      className: "SouqPickerGrid"
    }, this.pickerButton('🛒', 'Buy & Sell', 'Sell an item', 'buy-sell'), this.pickerButton('📦', 'Final Exit Sale', 'Leaving Saudi?', 'final-exit-sale'), this.pickerButton('🔍', 'Looking For', 'Find an item', 'looking-for'), this.pickerButton('🎁', 'Free & Giveaways', 'Give something away', 'free-giveaways')))), this.itemCategoryPickerOpen && m("div", {
      className: "SouqCategoryPicker SouqItemCategoryPicker"
    }, m("button", {
      className: "SouqPickerBack",
      onclick: () => this.backToListingTypes()
    }, "\u2190 Back"), m("h3", null, "Choose a category"), m("p", null, "What type of item are you posting?"), m("div", {
      className: "SouqPickerGrid"
    }, this.itemCategoryButton('🚗', 'Vehicles', 'Cars, motorcycles & vehicle items', 'vehicles'), this.itemCategoryButton('📱', 'Phones & Electronics', 'Phones, laptops, gadgets & electronics', 'phones-electronics'), this.itemCategoryButton('👕', 'Fashion', 'Clothing, shoes, bags & accessories', 'fashion'), this.itemCategoryButton('🏠', 'Home & Living', 'Furniture, appliances & household items', 'home-living'), this.itemCategoryButton('⚽', 'Sports & Hobbies', 'Sports gear, hobbies & recreation', 'sports-hobbies'), this.itemCategoryButton('⌚', 'Jewelry & Watches', 'Watches, jewelry & accessories', 'jewelry-watches'), this.itemCategoryButton('🧸', 'Baby & Kids', 'Baby gear, toys & children items', 'baby-kids'), this.itemCategoryButton('📦', 'Other Items', 'Everything else', 'other-items'))))), m("section", {
      className: "SouqDisclaimer"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "SouqDisclaimer-box"
    }, m("h3", null, "\uD83D\uDEE1\uFE0F Souq Safety & Posting Rules"), m("p", null, "Listings on PinoyTalks Souq are created and posted by individual community members. PinoyTalks does not own, sell, inspect, verify, endorse, guarantee, or process payments for listed items or transactions."), m("p", null, "Buyers and sellers are responsible for verifying the item, seller or buyer, condition, legality, price, payment, pickup, delivery, and other transaction details before completing any deal."), m("p", null, "Illegal, restricted, fraudulent, counterfeit, stolen, misleading, unsafe, or otherwise prohibited items and services are not allowed. PinoyTalks may remove listings without notice and may restrict or suspend accounts when necessary."), m("p", {
      className: "SouqDisclaimer-small"
    }, "PinoyTalks is a community classifieds platform and is not a party to transactions between buyers and sellers.")))), m("main", {
      className: "container SouqMain"
    }, m("section", {
      className: "SouqIntro"
    }, m("h2", null, "Browse by Category"), m("p", null, "Find items from the PinoyTalks OFW community.")), m("section", {
      className: "SouqCategories SouqCategories--items"
    }, this.category('🚗', 'Vehicles', 'Cars, motorcycles & vehicle items', 'vehicles'), this.category('📱', 'Phones & Electronics', 'Phones, laptops, gadgets & electronics', 'phones-electronics'), this.category('👕', 'Fashion', 'Clothing, shoes, bags & accessories', 'fashion'), this.category('🏠', 'Home & Living', 'Furniture, appliances & household items', 'home-living'), this.category('⚽', 'Sports & Hobbies', 'Sports gear, hobbies & recreation', 'sports-hobbies'), this.category('⌚', 'Jewelry & Watches', 'Watches, jewelry & accessories', 'jewelry-watches'), this.category('🧸', 'Baby & Kids', 'Baby gear, toys & children items', 'baby-kids'), this.category('📦', 'Other Items', 'Everything else', 'other-items')), m("section", {
      className: "SouqFilters"
    }, m("div", {
      className: "SouqFiltersHeader"
    }, m("div", null, m("h2", null, "Filter Listings"), m("p", null, "Narrow down the Souq listings you want to see.")), m("button", {
      className: "SouqClearFilters",
      onclick: () => this.clearFilters()
    }, "Clear Filters")), m("label", {
      className: "SouqKeywordSearch"
    }, m("span", null, "Search Souq"), m("div", {
      className: "SouqKeywordSearch-input"
    }, m("span", {
      className: "SouqKeywordSearch-icon"
    }, "\uD83D\uDD0E"), m("input", {
      type: "search",
      placeholder: "Search iPhone, fridge, bed, Samsung...",
      value: this.pendingFilters.keyword,
      oninput: event => {
        this.pendingFilters.keyword = event.target.value;
      },
      onkeydown: event => {
        if (event.key === 'Enter') {
          this.applyFilters();
        }
      }
    }))), m("div", {
      className: "SouqFilterGrid"
    }, m("label", {
      className: "SouqFilterField"
    }, m("span", null, "Item Category"), m("select", {
      value: this.pendingFilters.itemCategory,
      onchange: event => {
        this.pendingFilters.itemCategory = event.target.value;
      }
    }, m("option", {
      value: "all"
    }, "All Item Categories"), m("option", {
      value: "vehicles"
    }, "Vehicles"), m("option", {
      value: "phones-electronics"
    }, "Phones & Electronics"), m("option", {
      value: "fashion"
    }, "Fashion"), m("option", {
      value: "home-living"
    }, "Home & Living"), m("option", {
      value: "sports-hobbies"
    }, "Sports & Hobbies"), m("option", {
      value: "jewelry-watches"
    }, "Jewelry & Watches"), m("option", {
      value: "baby-kids"
    }, "Baby & Kids"), m("option", {
      value: "other-items"
    }, "Other Items"))), m("label", {
      className: "SouqFilterField"
    }, m("span", null, "Listing Type"), m("select", {
      value: this.pendingFilters.category,
      onchange: event => {
        this.pendingFilters.category = event.target.value;
      }
    }, m("option", {
      value: "all"
    }, "All Categories"), m("option", {
      value: "buy-sell"
    }, "Buy & Sell"), m("option", {
      value: "final-exit-sale"
    }, "Final Exit Sale"), m("option", {
      value: "looking-for"
    }, "Looking For"), m("option", {
      value: "free-giveaways"
    }, "Free & Giveaways"))), m("label", {
      className: "SouqFilterField"
    }, m("span", null, "Status"), m("select", {
      value: this.pendingFilters.status,
      onchange: event => {
        this.pendingFilters.status = event.target.value;
      }
    }, m("option", {
      value: "all"
    }, "All Statuses"), m("option", {
      value: "active"
    }, "Active"), m("option", {
      value: "reserved"
    }, "Reserved"), m("option", {
      value: "sold"
    }, "Sold"))), m("label", {
      className: "SouqFilterField"
    }, m("span", null, "Location"), m("input", {
      type: "text",
      placeholder: "e.g. Dammam",
      value: this.pendingFilters.location,
      oninput: event => {
        this.pendingFilters.location = event.target.value;
      },
      onkeydown: event => {
        if (event.key === 'Enter') {
          this.applyFilters();
        }
      }
    })), m("label", {
      className: "SouqFilterField"
    }, m("span", null, "Min Price"), m("input", {
      type: "number",
      min: "0",
      placeholder: "SAR",
      value: this.pendingFilters.minPrice,
      oninput: event => {
        this.pendingFilters.minPrice = event.target.value;
      }
    })), m("label", {
      className: "SouqFilterField"
    }, m("span", null, "Max Price"), m("input", {
      type: "number",
      min: "0",
      placeholder: "SAR",
      value: this.pendingFilters.maxPrice,
      oninput: event => {
        this.pendingFilters.maxPrice = event.target.value;
      }
    }))), m("div", {
      className: "SouqFilterActions"
    }, m("button", {
      className: "SouqApplyFilters",
      onclick: () => this.applyFilters()
    }, "\uD83D\uDD0E SHOW RESULTS"), m("button", {
      className: "SouqClearFilters SouqClearFilters--bottom",
      onclick: () => this.clearFilters()
    }, "Clear Filters")), m("div", {
      className: "SouqFilterCount"
    }, "Showing", ' ', m("strong", null, filteredListings.length), ' ', "matching among", ' ', m("strong", null, this.discussions.length), ' ', "loaded listings")), m("section", {
      className: "SouqLatest"
    }, m("div", {
      className: "SouqSectionHeader"
    }, m("div", null, m("h2", null, "Latest Listings"), m("p", null, "Fresh listings from the community.")), m("a", {
      href: "/t/souq",
      className: "SouqViewAll"
    }, "View all \u2192")), this.loading ? m("div", {
      className: "SouqEmpty"
    }, "Loading Souq listings...") : this.listingError ? m("div", {
      className: "SouqEmpty"
    }, "Unable to load listings right now.") : filteredListings.length ? m("div", {
      className: "SouqListingResults"
    }, m("div", {
      className: "SouqListings"
    }, filteredListings.map(discussion => this.listingCard(discussion))), this.hasMore && m("div", {
      className: "SouqLoadMoreWrap"
    }, m("button", {
      className: "SouqLoadMoreButton",
      disabled: this.loadingMore,
      onclick: () => this.loadMoreListings()
    }, this.loadingMore ? 'LOADING...' : 'LOAD MORE LISTINGS'), m("div", {
      className: "SouqLoadMoreHint"
    }, "Showing the newest listings first"))) : m("div", {
      className: "SouqEmpty"
    }, m("div", {
      className: "SouqEmpty-icon"
    }, "\uD83D\uDECD\uFE0F"), m("h3", null, this.discussions.length ? 'No listings match your filters.' : 'PinoyTalks Souq is open!'), m("p", null, this.discussions.length ? 'Try changing or clearing your filters.' : 'Be one of the first members to post an item.'), this.discussions.length ? m("div", {
      className: "SouqEmptyActions"
    }, m("button", {
      className: "SouqEmptyButton",
      onclick: () => this.clearFilters()
    }, "Clear Filters"), this.hasMore && m("button", {
      className: "SouqLoadMoreButton SouqLoadMoreButton--secondary",
      disabled: this.loadingMore,
      onclick: () => this.loadMoreListings()
    }, this.loadingMore ? 'SEARCHING...' : 'SEARCH OLDER LISTINGS')) : m("button", {
      className: "SouqEmptyButton",
      onclick: () => this.postItem()
    }, "Post Your First Item"))), m("section", {
      className: "SouqSafety"
    }, m("div", {
      className: "SouqSafety-icon"
    }, "\uD83D\uDEE1\uFE0F"), m("div", null, m("strong", null, "Buy and sell safely"), m("p", null, "Meet in a safe location and verify the item before paying. PinoyTalks does not process payments, hold funds, or guarantee transactions.")))));
  }
}

// Reuse the existing actions without mounting a marketplace Page or its composer timers.
class SouqListingDetailControls extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.actions = Object.create(SouqPage.prototype);
    for (const key of ['expiryData', 'expiryLoading', 'renewingListings', 'contactAvailability', 'contactAvailabilityLoading', 'contactSaving', 'updatingStatus']) {
      this.actions[key] = {};
    }
    const discussion = this.attrs.discussion;
    this.actions.loadListings = () => app.store.find('discussions', discussion.id(), {
      include: 'tags,user,firstPost'
    });
    this.actions.ensureExpiryLoaded(discussion);
    if (this.actions.canManageStatus(discussion)) this.actions.ensureContactAvailability(discussion);
  }
  view() {
    return this.actions.listingDetailControls(this.attrs.discussion);
  }
}
extend(CommentPost.prototype, 'content', function (content) {
  const post = this.attrs.post;
  const discussion = post.discussion();
  if (app.current.matches(DiscussionPage) && post.number() === 1 && isSouqDiscussion(discussion)) {
    content.push(m(SouqListingDetailControls, {
      discussion: discussion
    }));
  }
});

// Core search/category/home discussion previews must not expose listing body text.
extend(DiscussionListItem.prototype, 'infoItems', function (items) {
  if (isSouqDiscussion(this.attrs.discussion)) items.remove('excerpt');
});
extend(DiscussionsSearchItem.prototype, 'viewItems', function (items) {
  if (isSouqDiscussion(this.discussion)) items.remove('most-relevant');
});
flarum.reg.add('pinoytalks-souq', 'forum/components/SouqPage', SouqPage);flarum.reg.addChunkModule('170', '985', 'pinoytalks-souq', 'forum/components/SouqFlagPostModal');
flarum.reg.addChunkModule('170', '985', 'pinoytalks-souq', 'forum/index');
flarum.reg.addChunkModule('170', '985', 'pinoytalks-souq', 'forum/extend');
flarum.reg.addChunkModule('170', '985', 'pinoytalks-souq', 'forum/components/SouqPage');
flarum.reg.addChunkModule('170', '985', 'pinoytalks-souq', 'forum/SouqGallery');